import { useMutation } from '@tanstack/react-query'
import React from 'react'

import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import useDialog from '@/hooks/useDialog'
import { useInvalidatePanelQueries } from '@/hooks/useInvalidatePanelQueries'
import useNotyf from '@/hooks/useNotyf'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { CUSTOM_EVENT } from '@/lib/constants'
import commercialAgreementTemplateService from '@/lib/services/commercialAgreementTemplateService'

interface Props {
  agreementTemplateId: number
}

const RecordNoCommercialTermsButton: React.FC<Props> = ({
  agreementTemplateId,
}) => {
  const confirmDialog = useDialog()
  const showGenericServerError = useShowGenericServerError()
  const notyf = useNotyf()
  const invalidatePanelsQuery = useInvalidatePanelQueries()

  const recordNoTermsMutation = useMutation({
    mutationFn: () =>
      commercialAgreementTemplateService.recordNoTerms(agreementTemplateId),
    onSuccess: async () => {
      await invalidatePanelsQuery()
      notyf.success('No commercial terms recorded')
      confirmDialog.hideDialog()
      window.dispatchEvent(
        new CustomEvent(CUSTOM_EVENT.commercialAgreementTemplateReviewed),
      )
    },
    onError: showGenericServerError,
  })

  const isProcessing = recordNoTermsMutation.isPending

  return (
    <>
      <Button variant="outline-secondary" onClick={confirmDialog.showDialog}>
        Record no commercial terms
      </Button>

      <Dialog
        open={confirmDialog.isShowingDialog}
        onHide={confirmDialog.hideDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <p>Are you sure you want to record no commercial terms?</p>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline-secondary"
              onClick={confirmDialog.hideDialog}
            >
              Cancel
            </Button>

            <ActionButton
              variant="success"
              onClick={() => recordNoTermsMutation.mutate()}
              isProcessing={isProcessing}
            >
              Yes, record no terms
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RecordNoCommercialTermsButton
